/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

'use client';

import { getActiveLocale } from '../../utils/locale';

/**
 * Formats an ISO timestamp string into the display format: "7 Apr 11:42AM"
 * - Day as a number (no leading zero)
 * - Month as 3-letter abbreviation
 * - Time in 12-hour format with AM/PM, no space before AM/PM
 * - No year shown
 *
 * Returns null if the timestamp is missing or invalid.
 */
function formatTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null;

  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;

  const activeLocale = getActiveLocale();
  return new Intl.DateTimeFormat(activeLocale, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

interface TimeRangeProps {
  /** ISO timestamp string for the start time */
  startTime?: string | null;
  /** ISO timestamp string for the end time */
  endTime?: string | null;
  className?: string;
}

/**
 * Renders a formatted time range: "7 Apr 11:42AM – 7 Apr 11:43AM"
 * If only one side is present, renders just that side.
 * If neither is present, renders nothing.
 */
export default function TimeRange({ startTime, endTime, className }: TimeRangeProps) {
  const formattedStart = formatTimestamp(startTime);
  const formattedEnd = formatTimestamp(endTime);

  if (!formattedStart && !formattedEnd) return null;

  return (
    <div className={className ?? 'text-body-subtle text-body'}>
      {formattedStart}
      {formattedStart && formattedEnd ? ' - ' : ''}
      {formattedEnd}
    </div>
  );
}
